<script>
  export let images = [];
  /** Called after slide changes (e.g. parent may pan map to keep label in view). */
  export let onSlideChange = () => {};

  import { onDestroy, onMount, tick } from "svelte";
  import { Splide, SplideSlide } from "@splidejs/svelte-splide";
  import "@splidejs/splide/dist/css/splide.min.css";
  import Icon from "@iconify/svelte";

  let splideComponent;
  let frameEl;
  let resizeObserver;
  let isSingleImage = false;

  $: isSingleImage = images.length <= 1;
  $: imagesKey = images.map((img) => img.src).join("|");

  $: splideOptions = {
    type: "slide",
    pagination: false,
    rewind: !isSingleImage,
    perMove: 1,
    perPage: 1,
    arrows: false,
    drag: images.length > 1,
    width: "100%",
  };

  function scheduleSplideRefresh() {
    const splide = splideComponent?.splide;
    if (!splide) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        splide.refresh();
      });
    });
  }

  function goPrev() {
    splideComponent?.splide?.go("<");
    onSlideChange();
  }

  function goNext() {
    splideComponent?.splide?.go(">");
    onSlideChange();
  }

  onMount(async () => {
    await tick();
    const splide = splideComponent?.splide;
    if (splide) {
      splide.on("mounted", scheduleSplideRefresh);
      splide.on("moved", () => onSlideChange());
      scheduleSplideRefresh();
    }
    if (frameEl && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => scheduleSplideRefresh());
      resizeObserver.observe(frameEl);
    }
  });

  // {#key imagesKey} remounts Splide without re-running onMount
  $: if (imagesKey && splideComponent?.splide) {
    tick().then(scheduleSplideRefresh);
  }

  onDestroy(() => {
    resizeObserver?.disconnect();
    splideComponent?.destroy?.();
  });
</script>

{#if images.length > 0}
  <div class="map-floating-inline-gallery" class:has-nav={!isSingleImage}>
    {#if !isSingleImage}
      <button
        type="button"
        class="map-floating-inline-gallery-arrow map-floating-inline-gallery-arrow-prev"
        on:click|stopPropagation={goPrev}
        aria-label="Previous image"
      >
        <Icon icon="material-symbols:chevron-left-rounded" width="18" height="18" />
      </button>
    {/if}

    <div class="map-floating-inline-gallery-frame" bind:this={frameEl}>
      {#key imagesKey}
        <Splide options={splideOptions} bind:this={splideComponent}>
          {#each images as image (image.src)}
            <SplideSlide>
              <img
                src={image.src}
                alt={image.alt ?? ""}
                class="map-floating-inline-gallery-img"
                on:load={scheduleSplideRefresh}
              />
            </SplideSlide>
          {/each}
        </Splide>
      {/key}
    </div>

    {#if !isSingleImage}
      <button
        type="button"
        class="map-floating-inline-gallery-arrow map-floating-inline-gallery-arrow-next"
        on:click|stopPropagation={goNext}
        aria-label="Next image"
      >
        <Icon icon="material-symbols:chevron-right-rounded" width="18" height="18" />
      </button>
    {/if}
  </div>
{/if}

<style>
  .map-floating-inline-gallery {
    position: relative;
    width: 100%;
    box-sizing: border-box;
  }

  .map-floating-inline-gallery-frame {
    position: relative;
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 12px;
    overflow: hidden;
    background: #000;
  }

  .map-floating-inline-gallery-frame::before,
  .map-floating-inline-gallery-frame::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    width: 20%;
    max-width: 72px;
    z-index: 1;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .has-nav .map-floating-inline-gallery-frame::before,
  .has-nav .map-floating-inline-gallery-frame::after {
    opacity: 1;
  }

  .map-floating-inline-gallery-frame::before {
    left: 0;
    background: linear-gradient(to right, rgba(0, 0, 0, 0.35), transparent);
  }

  .map-floating-inline-gallery-frame::after {
    right: 0;
    background: linear-gradient(to left, rgba(0, 0, 0, 0.35), transparent);
  }

  .map-floating-inline-gallery-frame :global(.splide),
  .map-floating-inline-gallery-frame :global(.splide__track),
  .map-floating-inline-gallery-frame :global(.splide__list),
  .map-floating-inline-gallery-frame :global(.splide__slide) {
    height: 100%;
  }

  .map-floating-inline-gallery-frame :global(.splide) {
    width: 100%;
  }

  .map-floating-inline-gallery-frame :global(.splide__track) {
    overflow: hidden;
  }

  .map-floating-inline-gallery-frame :global(.splide__slide) {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #000;
  }

  .map-floating-inline-gallery-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .map-floating-inline-gallery-arrow {
    position: absolute;
    top: 50%;
    z-index: 2;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.28);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    color: rgba(255, 255, 255, 0.82);
    opacity: 0.72;
    cursor: pointer;
    box-shadow: none;
    transition:
      opacity 0.2s ease,
      background 0.2s ease,
      color 0.2s ease;
  }

  .map-floating-inline-gallery-arrow:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.42);
    color: #fff;
  }

  .map-floating-inline-gallery-arrow:active {
    opacity: 0.88;
    background: rgba(0, 0, 0, 0.5);
  }

  .map-floating-inline-gallery-arrow:focus-visible {
    opacity: 1;
    outline: 1px solid rgba(255, 255, 255, 0.45);
    outline-offset: 2px;
  }

  .map-floating-inline-gallery-arrow-prev {
    left: 10px;
  }

  .map-floating-inline-gallery-arrow-next {
    right: 10px;
  }
</style>
